import React, {Component} from "react";
import {Dropdown} from "primereact/dropdown";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {NumberInput} from "../../components";
import {FormField} from "../../components/form";
import EnumCoPayUnit from "./enums/EnumCoPayUnit";
import MoneyUtil from "../../utils/MoneyUtil";
import CoPayCptSelector from "./CoPayCptSelector";
import "./CoPayRulesTable.css";

/**
 * Editable (or read-only) co-pay table: a pinned "All other CPTs" default row plus
 * a list of CPT-specific exception rules. Each row carries its own co-pay unit and
 * max-co-pay unit.
 *
 * Props:
 *  - defaultRule: {coPay, coPayUnit, coPayMax, coPayMaxUnit}
 *  - rules: [{cpts, coPay, coPayUnit, coPayMax, coPayMaxUnit}]
 *  - onDefaultChange(field, value)
 *  - onAddRule(), onUpdateRule(index, field, value), onRemoveRule(index)
 *  - registerField(formField): register a FormField for validation (edit mode)
 *  - readOnly: render as static text (view mode)
 */
export default class CoPayRulesTable extends Component {

    units = EnumCoPayUnit.toArray();

    register = (formField) => {
        if (this.props.registerField) {
            this.props.registerField(formField);
        }
    };

    excludeCodesFor(ruleIndex) {
        const codes = [];
        (this.props.rules || []).forEach((rule, index) => {
            if (index === ruleIndex) return;
            (rule.cpts || []).forEach(cpt => codes.push(cpt.cptCode));
        });
        return codes;
    }

    renderUnitCell(rule, field, onChange, required) {
        if (this.props.readOnly) {
            return <span>{EnumCoPayUnit[rule[field]] || "-"}</span>;
        }
        const dropdown = <Dropdown optionLabel="name" optionValue="key"
                                   appendTo={document.body}
                                   value={rule[field]}
                                   options={this.units}
                                   onChange={e => onChange(field, e.value)}/>;
        return required ?
            <FormField ref={this.register} required validateOn={rule[field]}>{dropdown}</FormField> :
            dropdown;
    }

    renderAmountCell(rule, field, unitField, onChange) {
        if (this.props.readOnly) {
            return <span>{rule[field] != null ? rule[field] : "-"}</span>;
        }
        return <NumberInput value={rule[field]}
                            allowDecimal={true}
                            maxFractionDigits={MoneyUtil.getFractionDigits(rule[unitField])}
                            onChange={value => onChange(field, value)}/>;
    }

    renderValueCells(rule, onChange, requireCoPay) {
        const coPayInput = this.renderAmountCell(rule, "coPay", "coPayUnit", onChange);
        return (
            <>
                <td>
                    {requireCoPay && !this.props.readOnly ?
                        <FormField ref={this.register} required validateOn={rule.coPay} min={0}>
                            {coPayInput}
                        </FormField> : coPayInput}
                </td>
                <td>{this.renderUnitCell(rule, "coPayUnit", onChange, requireCoPay)}</td>
                <td>{this.renderAmountCell(rule, "coPayMax", "coPayMaxUnit", onChange)}</td>
                <td>{this.renderUnitCell(rule, "coPayMaxUnit", onChange, false)}</td>
            </>
        );
    }

    renderRuleRow(rule, index) {
        const onChange = (field, value) => this.props.onUpdateRule(index, field, value);
        return (
            <tr key={index}>
                <td className="copay-cpt-cell">
                    {this.props.readOnly ?
                        <CoPayCptSelector readOnly value={rule.cpts}/> :
                        <FormField ref={this.register} required validateOn={rule.cpts && rule.cpts.length}>
                            <CoPayCptSelector value={rule.cpts}
                                              excludeCodes={this.excludeCodesFor(index)}
                                              onCptsChange={cpts => this.props.onUpdateRule(index, "cpts", cpts)}/>
                        </FormField>}
                </td>
                {this.renderValueCells(rule, onChange, true)}
                <td className="copay-action-cell">
                    {!this.props.readOnly &&
                        <FontAwesomeIcon icon={["fas", "trash"]}
                                         className="copay-delete-icon"
                                         onClick={() => this.props.onRemoveRule(index)}/>}
                </td>
            </tr>
        );
    }

    renderDefaultRow() {
        const rule = this.props.defaultRule || {};
        const onChange = (field, value) => this.props.onDefaultChange(field, value);
        return (
            <tr className="copay-default-row">
                <td className="copay-cpt-cell">
                    <FontAwesomeIcon icon={["fas", "lock"]} className="copay-lock-icon"/> All other CPTs
                </td>
                {this.renderValueCells(rule, onChange, true)}
                <td className="copay-action-cell"/>
            </tr>
        );
    }

    render() {
        const rules = this.props.rules || [];
        return (
            <div className="copay-rules-table">
                <table>
                    <thead>
                        <tr>
                            <th className="copay-cpt-cell">CPT(s)</th>
                            <th>Co-Pay</th>
                            <th>Unit</th>
                            <th>Max Co-Pay</th>
                            <th>Max Unit</th>
                            <th className="copay-action-cell"/>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule, index) => this.renderRuleRow(rule, index))}
                        {this.renderDefaultRow()}
                    </tbody>
                </table>
                {!this.props.readOnly &&
                    <div className="copay-add-rule" onClick={this.props.onAddRule}>
                        <FontAwesomeIcon icon={["fas", "plus"]}/> Add rule
                    </div>}
            </div>
        );
    }
}
